import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { logementAPI } from '../../service/api';
import { useAuth } from '../../contexts/AuthContext';
import { FiMapPin, FiUser, FiHome, FiArrowLeft, FiHeart, FiChevronLeft, FiChevronRight, FiX, FiEdit2, FiExternalLink } from 'react-icons/fi';
import { resolveUploadUrl } from '../../config/config';
import { toast } from '../../service/swal';
import PageMeta from '../../components/PageMeta';
import useAutoRefresh from '../../hooks/useAutoRefresh';

export default function LogementDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [logement, setLogement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favLoading, setFavLoading] = useState(false);

  const fetchLogement = async (skipLoading = false) => {
    if (!skipLoading) setLoading(true);
    try {
      const res = await logementAPI.getById(id);
      setLogement(res.data.data);
      setImgError(false);
      setCurrentImage(0);
    } catch (err) { console.error(err); }
    finally { if (!skipLoading) setLoading(false); }
  };

  useEffect(() => { fetchLogement(); }, [id]);
  useAutoRefresh(() => fetchLogement(true), 30000);

  useEffect(() => {
    if (user?.role === 'student') {
      logementAPI.getMyFavorites()
        .then(r => setIsFavorite(r.data.data?.some(p => p.id === parseInt(id))))
        .catch(() => {});
    }
  }, [id, user]);

  const toggleFavorite = async () => {
    if (favLoading) return;
    setFavLoading(true);
    try {
      if (isFavorite) {
        await logementAPI.removeFavorite(id);
        setIsFavorite(false);
        toast('success', 'Retiré des favoris');
      } else {
        await logementAPI.addFavorite(id);
        setIsFavorite(true);
        toast('success', 'Ajouté aux favoris');
      }
    } catch (err) { toast('error', 'Erreur'); }
    finally { setFavLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2" style={{ borderColor: '#C59B6C', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!logement) {
    return (
      <div className="page-container py-20 text-center">
        <p style={{ color: '#7A828A' }}>Logement non trouvé</p>
        <Link to="/logements" className="btn-outline-root mt-4 inline-block">Retour</Link>
      </div>
    );
  }

  const typeLabels = { studio: 'Studio', appartement: 'Appartement', maison: 'Maison', chambre: 'Chambre' };

  return (
    <div className="page-container py-8">
      <PageMeta title="Détail du logement" />
      <Link to="/logements" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider mb-6"
        style={{ color: '#7A828A' }}>
        <FiArrowLeft /> Retour
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="relative">
            <div className="h-80 flex items-center justify-center overflow-hidden cursor-pointer" style={{ backgroundColor: '#E5E5E0' }}
              onClick={() => !imgError && logement.images?.length > 0 && setLightboxOpen(true)}>
              {logement.images && logement.images.length > 0 && !imgError ? (
                <img src={resolveUploadUrl(logement.images[currentImage])} alt={logement.titre} className="w-full h-full object-cover" onError={() => setImgError(true)} />
              ) : (
                <FiHome className="w-20 h-20" style={{ color: '#88929B' }} />
              )}
            </div>
            {logement.images && logement.images.length > 1 && !imgError && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                <button onClick={(e) => { e.stopPropagation(); setCurrentImage((prev) => (prev - 1 + logement.images.length) % logement.images.length); setImgError(false); }}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-white shadow transition">
                  <FiChevronLeft />
                </button>
                <div className="flex gap-1.5">
                  {logement.images.map((_, i) => (
                    <button key={i} onClick={(e) => { e.stopPropagation(); setCurrentImage(i); setImgError(false); }}
                      className={`w-2 h-2 rounded-full transition ${i === currentImage ? 'bg-navy' : 'bg-white/60'}`} />
                  ))}
                </div>
                <button onClick={(e) => { e.stopPropagation(); setCurrentImage((prev) => (prev + 1) % logement.images.length); setImgError(false); }}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-white shadow transition">
                  <FiChevronRight />
                </button>
              </div>
            )}
          </div>

          {lightboxOpen && logement.images && logement.images.length > 0 && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90" onClick={() => setLightboxOpen(false)}>
              <button className="absolute top-4 right-4 text-white hover:text-brass transition" onClick={() => setLightboxOpen(false)}>
                <FiX className="w-8 h-8" />
              </button>
              <button className="absolute left-4 text-white hover:text-brass transition" onClick={(e) => { e.stopPropagation(); setCurrentImage((prev) => (prev - 1 + logement.images.length) % logement.images.length); setImgError(false); }}>
                <FiChevronLeft className="w-10 h-10" />
              </button>
              <img src={resolveUploadUrl(logement.images[currentImage])} alt={logement.titre} className="max-w-[90vw] max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} onError={() => setImgError(true)} />
              <button className="absolute right-4 text-white hover:text-brass transition" onClick={(e) => { e.stopPropagation(); setCurrentImage((prev) => (prev + 1) % logement.images.length); setImgError(false); }}>
                <FiChevronRight className="w-10 h-10" />
              </button>
            </div>
          )}

          <div className="mt-6">
            <div className="flex items-center gap-3 mb-2">
              <span className="badge-root">{typeLabels[logement.type] || logement.type}</span>
              <span className={`text-[11px] font-bold tracking-widest uppercase px-3 py-1 border-2 ${
                logement.statut === 'disponible' ? 'border-green-600 text-green-600' :
                logement.statut === 'reserve' ? 'border-yellow-600 text-yellow-600' : 'border-red-600 text-red-600'
              }`}>
                {logement.statut === 'disponible' ? 'Disponible' : logement.statut === 'reserve' ? 'Réservé' : 'Loué'}
              </span>
            </div>
            <h1 className="font-serif text-3xl font-bold tracking-tight mt-3" style={{ color: '#0A192F' }}>{logement.titre}</h1>
            <p className="flex items-center gap-1 text-sm mt-2" style={{ color: '#7A828A' }}>
              <FiMapPin /> {logement.adresse}
            </p>
          </div>

          <div className="mt-8">
            <h2 className="font-serif text-xl font-bold" style={{ color: '#0A192F' }}>Description</h2>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: '#5A626A' }}>{logement.description}</p>
          </div>

          {logement.surface && (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="card-root p-4 text-center">
                <p className="text-2xl font-bold" style={{ color: '#0A192F' }}>{logement.surface} m²</p>
                <p className="label-root mt-1">Surface</p>
              </div>
              {logement.nbPieces && (
                <div className="card-root p-4 text-center">
                  <p className="text-2xl font-bold" style={{ color: '#0A192F' }}>{logement.nbPieces}</p>
                  <p className="label-root mt-1">Pièces</p>
                </div>
              )}
            </div>
          )}

          {logement.mapsEmbedUrl && (
            <div className="mt-6">
              <h2 className="font-serif text-xl font-bold mb-3 flex items-center gap-2" style={{ color: '#0A192F' }}>
                <FiMapPin /> Localisation
              </h2>
              <div className="overflow-hidden" style={{ border: '2px solid #E5E5E0' }}>
                <div dangerouslySetInnerHTML={{ __html: logement.mapsEmbedUrl }} />
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="card-root p-6" style={{ position: 'sticky', top: '6rem' }}>
            <p className="text-3xl font-bold" style={{ color: '#C59B6C' }}>
              {Number(logement.prix).toLocaleString()} $ <span className="text-sm" style={{ color: '#7A828A' }}>/ mois</span>
            </p>
            <hr className="my-6" style={{ borderColor: '#E5E5E0' }} />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#F4F4F0' }}>
                <FiUser className="w-5 h-5" style={{ color: '#0A192F' }} />
              </div>
              <div>
                <p className="text-sm font-bold" style={{ color: '#0A192F' }}>{logement.ownerName}</p>
                <p className="label-root">Propriétaire</p>
              </div>
            </div>
            <hr className="my-6" style={{ borderColor: '#E5E5E0' }} />
            {user && (user.role === 'admin' || user.role === 'owner') && (
              <Link to={`/logements/${logement.id}/editer`} className="btn-outline-root w-full py-3 flex items-center justify-center gap-2 mb-3">
                <FiEdit2 /> Modifier
              </Link>
            )}
            {user ? (
              <button className={`w-full py-3 flex items-center justify-center gap-2 ${isFavorite ? 'btn-root' : 'btn-primary-root'}`}
                style={isFavorite ? { borderColor: '#C62828', color: '#C62828' } : {}}
                onClick={user.role === 'student' ? toggleFavorite : undefined}>
                <FiHeart style={isFavorite ? { fill: '#C62828' } : {}} />
                {isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
              </button>
            ) : (
              <Link to="/login" className="btn-primary-root w-full py-3 text-center block">
                Connectez-vous pour contacter
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
